// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.

/**
 * The hidden decoy field every public form on this site carries.
 *
 * One module, not one copy per form: a bot learns a site's shape, so the
 * careers-interest form and the job-application form must present the SAME
 * decoy under the same name. Two hand-written checks would drift the moment
 * one of them was touched, and a drifted honeypot is a honeypot a crawler can
 * tell apart. `src/components/HoneypotField.tsx` is the markup half.
 *
 * Pure: no request object, no database, no framework.
 */

/**
 * The field name. Innocuous enough that a form-filling bot has a plausible
 * value to put in it — the point is that it fills the field at all.
 */
export const HONEYPOT_FIELD = "companyWebsite";

/** Trim, and read anything that is not a string as absent. */
function readString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Whether the decoy came back filled — meaning the caller is not a person.
 *
 * Accepts an ARRAY as well as a single value. A `multipart/form-data` body may
 * repeat a part name, and this endpoint's field reader collapses repeats into
 * an array; a check that only understood strings would read a doubled-up decoy
 * as empty, which is a one-line bypass for any bot that sent the field twice.
 *
 * Whitespace reads as empty. A person cannot reach the field at all, so the
 * only way a space gets there is an over-eager autofill, and treating that as
 * a bot would silently discard a real application.
 */
export function isHoneypotFilled(fields: Record<string, unknown>): boolean {
  const value = fields[HONEYPOT_FIELD];
  const values = Array.isArray(value) ? value : [value];
  return values.some((entry) => readString(entry).length > 0);
}
